import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, Trash2, ArrowLeft } from 'lucide-react'
import type { BenefitRule } from '../types'
import {
  TRIGGER_TYPE_LABELS,
  ACTION_TYPE_LABELS,
  RECURRENCE_LABELS,
  CATEGORY_OPTIONS,
  type RecurrenceType,
} from '../types'
import { ConfirmDialog } from '@/shared/components/ui/molecules/ConfirmDialog'

interface BenefitRuleDetailProps {
  rule: BenefitRule
  onDelete: () => void
  isDeleting: boolean
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function renderTriggerConfig(rule: BenefitRule) {
  const cfg = rule.trigger_config
  switch (rule.trigger_type) {
    case 'birthday_upcoming':
      return <p>{(cfg.days_before as number) ?? 7} días antes del cumpleaños</p>
    case 'category_reached':
      return <p>Categoría requerida: <span className="font-medium capitalize">{(cfg.category as string) ?? '-'}</span></p>
    case 'recurring_schedule':
      return (
        <div>
          <p>Recurrencia: {RECURRENCE_LABELS[(cfg.recurrence as RecurrenceType)] ?? (cfg.recurrence as string)}</p>
          <p>Zona horaria: {(cfg.timezone as string) ?? 'America/Santiago'}</p>
        </div>
      )
    case 'purchase_completed':
      return <p>Canal: <span className="capitalize">{(cfg.channel as string) ?? 'all'}</span></p>
    case 'client_created':
      return <p>Se activa al crear un cliente nuevo</p>
    default:
      return <p>-</p>
  }
}

function renderActionConfig(rule: BenefitRule) {
  const cfg = rule.action_config
  switch (rule.action_type) {
    case 'coupon':
      return (
        <div className="space-y-1">
          <p>Tipo: {(cfg.discount_type as string) === 'percent' ? 'Porcentaje' : 'Monto fijo'}</p>
          <p>Valor: {(cfg.discount_type as string) === 'percent'
            ? `${cfg.amount as number}%`
            : `$${(cfg.amount as number)?.toLocaleString('es-CL')}`}
          </p>
          {(cfg.max_discount as number) > 0 && (
            <p>Descuento máximo: ${(cfg.max_discount as number)?.toLocaleString('es-CL')}</p>
          )}
          {Boolean(cfg.code_prefix) && <p>Prefijo código: {String(cfg.code_prefix)}</p>}
        </div>
      )
    case 'physical_gift_voucher':
      return (
        <div className="space-y-1">
          <p>Descripción: {(cfg.gift_description as string) || '-'}</p>
          <p>Validez: {cfg.valid_days as number} días</p>
        </div>
      )
    case 'free_shipping_flag':
      return (
        <div>
          <p>Condición: {
            (cfg.condition as string) === 'first_purchase' ? 'Primera compra' :
            (cfg.condition as string) === 'min_amount' ? `Monto mínimo $${(cfg.min_amount as number)?.toLocaleString('es-CL')}` :
            'Permanente'
          }</p>
        </div>
      )
    case 'points_bonus':
      return <p>Puntos bonus: {cfg.bonus_points as number}</p>
    case 'email_notification':
      return (
        <div className="space-y-1">
          <p>Template: {(cfg.template as string) || '-'}</p>
          <p>Asunto: {(cfg.subject as string) || '-'}</p>
        </div>
      )
    default:
      return <p>-</p>
  }
}

function renderRulesConfig(rule: BenefitRule) {
  const cfg = rule.rules_config
  const categories = (cfg.eligible_categories as string[]) ?? []
  const channels = (cfg.valid_channels as string[]) ?? []

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div>
        <p className="text-sm text-gray-500">Categorías elegibles</p>
        <div className="flex gap-1 mt-1">
          {categories.map((cat) => (
            <span key={cat} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs capitalize">
              {cat}
            </span>
          ))}
          {categories.length === 0 && <span className="text-gray-400 text-sm">Todas</span>}
        </div>
      </div>
      <div>
        <p className="text-sm text-gray-500">Uso único</p>
        <p className="font-medium">{(cfg.single_use as boolean) ? 'Sí' : 'No'}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Validez</p>
        <p className="font-medium">
          {cfg.valid_days ? `${cfg.valid_days} días` : 'Sin límite'}
        </p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Max. otorgamientos/año</p>
        <p className="font-medium">
          {cfg.max_grants_per_year ? String(cfg.max_grants_per_year) : 'Sin límite'}
        </p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Max. otorgamientos/mes</p>
        <p className="font-medium">
          {cfg.max_grants_per_month ? String(cfg.max_grants_per_month) : 'Sin límite'}
        </p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Canales</p>
        <div className="flex gap-1 mt-1">
          {channels.map((ch) => (
            <span key={ch} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs capitalize">
              {ch}
            </span>
          ))}
        </div>
      </div>
      {cfg.min_purchase !== undefined && cfg.min_purchase !== null && (
        <div>
          <p className="text-sm text-gray-500">Compra mínima</p>
          <p className="font-medium">${(cfg.min_purchase as number)?.toLocaleString('es-CL')}</p>
        </div>
      )}
      {cfg.max_discount !== undefined && cfg.max_discount !== null && (
        <div>
          <p className="text-sm text-gray-500">Descuento máximo</p>
          <p className="font-medium">${(cfg.max_discount as number)?.toLocaleString('es-CL')}</p>
        </div>
      )}
      <div>
        <p className="text-sm text-gray-500">No combinable</p>
        <p className="font-medium">{(cfg.non_combinable as boolean) ? 'Sí' : 'No'}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Requiere email</p>
        <p className="font-medium">{(cfg.requires_email as boolean) ? 'Sí' : 'No'}</p>
      </div>
      {cfg.cooldown_days !== undefined && cfg.cooldown_days !== null && (
        <div>
          <p className="text-sm text-gray-500">Días de enfriamiento</p>
          <p className="font-medium">{String(cfg.cooldown_days)} días</p>
        </div>
      )}
      {(cfg.valid_until_first_purchase as boolean) && (
        <div>
          <p className="text-sm text-gray-500">Caduca al comprar</p>
          <p className="font-medium">Sí</p>
        </div>
      )}
    </div>
  )
}

export function BenefitRuleDetail({ rule, onDelete, isDeleting }: BenefitRuleDetailProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Suppress unused variable warnings — CATEGORY_OPTIONS is re-exported from types for use in form
  void CATEGORY_OPTIONS

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/benefits" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{rule.name}</h1>
            <span
              className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                rule.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {rule.is_active ? 'Activa' : 'Inactiva'}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/benefits/${rule.id}/edit`}
            className="btn btn-primary flex items-center gap-1.5"
          >
            <Pencil size={16} />
            Editar
          </Link>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5"
          >
            <Trash2 size={16} />
            Desactivar
          </button>
        </div>
      </div>

      {/* Description */}
      {rule.description && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-2">Descripción (visible al cliente)</h2>
          <p className="text-gray-700">{rule.description}</p>
        </div>
      )}

      {/* Internal Notes */}
      {rule.internal_notes && (
        <div className="card p-6 border-yellow-200 bg-yellow-50">
          <h2 className="text-lg font-semibold mb-2 text-yellow-800">Notas internas</h2>
          <p className="text-yellow-700">{rule.internal_notes}</p>
        </div>
      )}

      {/* Trigger Info */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Gatillo (Trigger)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Tipo</p>
            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
              {TRIGGER_TYPE_LABELS[rule.trigger_type]}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Configuración</p>
            <div className="text-gray-700">{renderTriggerConfig(rule)}</div>
          </div>
        </div>
      </div>

      {/* Action Info */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Acción (Beneficio)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Tipo</p>
            <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
              {ACTION_TYPE_LABELS[rule.action_type]}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Configuración</p>
            <div className="text-gray-700">{renderActionConfig(rule)}</div>
          </div>
        </div>
      </div>

      {/* Rules / Limitations */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Reglas y Limitaciones</h2>
        {renderRulesConfig(rule)}
      </div>

      {/* Metadata */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Metadata</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">ID</p>
            <p className="font-medium">{rule.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Prioridad</p>
            <p className="font-medium">{rule.priority}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Creada</p>
            <p className="font-medium">{formatDate(rule.created_at)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Actualizada</p>
            <p className="font-medium">{formatDate(rule.updated_at)}</p>
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Desactivar regla"
        message={`Está a punto de desactivar la regla "${rule.name}". Los beneficios ya otorgados no se verán afectados.`}
        confirmLabel="Desactivar"
        onConfirm={() => {
          setShowDeleteDialog(false)
          onDelete()
        }}
        onCancel={() => setShowDeleteDialog(false)}
        isLoading={isDeleting}
      />
    </div>
  )
}
